import { WorkActivityTypes } from '@shared/types';
import {
  Costume,
  Prop,
  Role,
  RoleActivity,
  RolePerformerType,
  RoleType,
  SessionActivity,
  SessionActivityType,
} from '@talon/web/lib/api';
import { useProjectStore, useRoleStore, useSessionActivityStore } from '@talon/web/src/context';
import { Box, Button, CheckBox, Layer, SelectMultiple, Text } from 'grommet';
import { Alert, Bike, Close, Cloud, Run, Tag, UserPolice } from 'grommet-icons';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TalonTip } from '..';

export interface IRoleActivityRowProps {
  activity: SessionActivity;
  roleId: string;
  updateSessionActivity: (update: Partial<SessionActivity>) => Promise<void>;
}
export const RoleActivityRow = ({ roleId, activity, updateSessionActivity }: IRoleActivityRowProps) => {
  const [role, setRole] = useState<Role | undefined>(undefined);
  const [propModalRole, setPropModalRole] = useState<Role | undefined>(undefined);
  const [costumeModalRole, setCostumeModalRole] = useState<Role | undefined>(undefined);
  const [roleActivity, setRoleActivity] = useState<RoleActivity | undefined>(undefined);
  const [roleActivityCostumes, setRoleActivityCostumes] = useState<Costume[]>([]);
  const [roleActivityProps, setRoleActivityProps] = useState<Prop[]>([]);
  const sessionActivityStore = useSessionActivityStore();
  const projectStore = useProjectStore();
  const roleStore = useRoleStore();

  const payScalePercent =
    role?.performer?.contracts[role.performer.contracts.length - 1].compensation[0].percentOfScale;

  const filterCostumesAndProps = () => {
    setRoleActivityCostumes(activity.costumes?.filter((c) => c.owner?.id === roleId) || []);
    setRoleActivityProps(activity.props?.filter((p) => p.owner?.id === roleId) || []);
  };

  useEffect(() => {
    if (!roleId) return;
    const getRole = async () => {
      const roleData = await roleStore.getRoleById(roleId);
      roleData && setRole(roleData);
    };
    getRole();
  }, [roleId]);

  useEffect(() => {
    if (activity.role_activities) {
      const ra = activity.role_activities.find((ra) => ra.role?.id === roleId);
      ra && setRoleActivity(ra);
    }
  }, [activity]);

  useEffect(() => {
    filterCostumesAndProps();
  }, [activity.costumes, activity.props, roleId]);

  const upsertRoleActivity = async (update: Partial<RoleActivity>) => {
    let ra;
    if (roleActivity) {
      ra = await sessionActivityStore.updateRoleActivity({ ...roleActivity, ...update });
    } else {
      ra = await sessionActivityStore.createRoleActivity({
        ...update,
        session_activity: activity,
        role: role,
        project: projectStore.currentProject,
      });
    }
    ra && setRoleActivity(ra);
  };

  const assignReimbursements = async ({ costumes, props }: { costumes?: Costume[]; props?: Prop[] }) => {
    console.log('assignReimbursements', activity, costumes, props);
    const otherRoleCostumes = activity.costumes?.filter((a) => a.owner?.id != roleId);
    const otherRoleProps = activity.props?.filter((a) => a.owner?.id != roleId);
    updateSessionActivity({
      ...activity,
      costumes: [...(otherRoleCostumes || []), ...(costumes || [])],
      props: [...(otherRoleProps || []), ...(props || [])],
    });
  };

  return role ? (
    <Box direction="row" align="center" justify="between" pad={{ horizontal: 'small' }}>
      <Text size="small">{role.name}</Text>
      <Box direction="row" align="center" gap="xxsmall" justify="end">
        {(payScalePercent ?? 0) > 100 && (
          <TalonTip content={<Text>{`Performer receives ${payScalePercent}% of scale.`}</Text>}>
            <Tag size="14" />
          </TalonTip>
        )}
        {role.costumes?.length > 0 && activity.type === SessionActivityType.WardrobeChange && (
          <Button
            onClick={() => {
              setCostumeModalRole(role);
            }}
            icon={<UserPolice color={roleActivityCostumes?.length ? 'accent-3' : 'light-3'} />}
            secondary
            size="small"
            tip={
              roleActivityCostumes.length > 0
                ? `${roleActivityCostumes.length} Costume(s) Selected for Reimbursement`
                : 'Select Costume Reimbursements'
            }
          />
        )}
        {role.props?.length > 0 && WorkActivityTypes.has(activity.type) && (
          <Button
            onClick={() => {
              setPropModalRole(role);
            }}
            icon={<Bike color={roleActivityProps?.length ? 'accent-3' : 'light-3'} />}
            secondary
            size="small"
            tip={
              roleActivityProps.length > 0
                ? `${roleActivityProps.length} Prop(s) Selected for Reimbursement`
                : 'Select Prop Reimbursements'
            }
          />
        )}
        {role.performer_type === RolePerformerType.OnCameraPrincipal && WorkActivityTypes.has(activity.type) && (
          <CheckBox
            name="own-stunts"
            checked={roleActivity ? roleActivity.is_own_stunt_performer : false}
            onChange={(event) => upsertRoleActivity({ is_own_stunt_performer: event.target.checked })}
          >
            {({ checked }: { checked: boolean }) => (
              <TalonTip content={<Text>Will this OCP do his/her own stunts?</Text>}>
                <Run color={checked ? 'accent-3' : 'light-3'} />
              </TalonTip>
            )}
          </CheckBox>
        )}
        {role.type === RoleType.SpecialtyDancer && (
          <CheckBox
            name="hazardous"
            checked={roleActivity ? roleActivity.is_hazardous_work : false}
            onChange={(event) => upsertRoleActivity({ is_hazardous_work: event.target.checked })}
          >
            {({ checked }: { checked: boolean }) => (
              <TalonTip content={<Text>Will this dancer work in hazardous conditions?</Text>}>
                <Alert color={checked ? 'accent-3' : 'light-3'} />
              </TalonTip>
            )}
          </CheckBox>
        )}
        {role.performer_type === RolePerformerType.Extra && (
          <CheckBox
            name="wet"
            checked={roleActivity ? roleActivity.is_wet_snow_smoke_dust_work : false}
            onChange={(event) => upsertRoleActivity({ is_wet_snow_smoke_dust_work: event.target.checked })}
          >
            {({ checked }: { checked: boolean }) => (
              <TalonTip content={<Text>Will this extra work in wet, snowy, smokey, or dusty conditions?</Text>}>
                <Cloud color={checked ? 'accent-3' : 'light-3'} />
              </TalonTip>
            )}
          </CheckBox>
        )}
      </Box>

      {costumeModalRole && (
        <Layer onClickOutside={() => setCostumeModalRole(undefined)} onEsc={() => setCostumeModalRole(undefined)}>
          <Box direction="column" pad="small" gap="small" width="large" height="small">
            <Box direction="row" pad="small" justify="between" align="center">
              <Text alignSelf="center">Select Costumes for {costumeModalRole.name}</Text>
              <Close onClick={() => setCostumeModalRole(undefined)} cursor="pointer" />
            </Box>
            <Box pad="small" direction="row" gap="small" justify="around" align="center">
              <SelectMultiple
                width={'xlarge'}
                options={costumeModalRole.costumes}
                value={roleActivityCostumes}
                valueKey="id"
                labelKey="name"
                placeholder={`Choose ${costumeModalRole.name}'s costumes for this activity...`}
                size="medium"
                onChange={({ value }) => assignReimbursements({ costumes: value })}
                showSelectedInline
                icon={
                  <TalonTip content={<Text>Choose Costumes</Text>}>
                    <UserPolice />
                  </TalonTip>
                }
              />
            </Box>
            <Text alignSelf="center">
              Need to add costumes? Add them <Link to="/project">on Project Home.</Link>
            </Text>
          </Box>
        </Layer>
      )}
      {propModalRole && (
        <Layer onClickOutside={() => setPropModalRole(undefined)} onEsc={() => setPropModalRole(undefined)}>
          <Box direction="column" pad="small" gap="small" width="large" height="small">
            <Box direction="row" pad="small" justify="between" align="center">
              <Text alignSelf="center">Select Props for {propModalRole.name}</Text>
              <Close onClick={() => setPropModalRole(undefined)} cursor="pointer" />
            </Box>
            <Box pad="small" direction="row" gap="small" justify="around" align="center">
              <SelectMultiple
                width={'xlarge'}
                options={propModalRole.props}
                value={roleActivityProps}
                valueKey="id"
                labelKey="name"
                placeholder={`Choose ${propModalRole.name}'s props for this activity...`}
                size="medium"
                onChange={({ value }) => assignReimbursements({ props: value })}
                showSelectedInline
                icon={
                  <TalonTip content={<Text>Choose Props</Text>}>
                    <Bike />
                  </TalonTip>
                }
              />
            </Box>
            <Text alignSelf="center">
              Need to add props? Add them <Link to="/project">on Project Home.</Link>
            </Text>
          </Box>
        </Layer>
      )}
    </Box>
  ) : (
    <></>
  );
};
